import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import convertToHTML from "draft-convert/lib/convertToHTML";
import selectn from "selectn";
import debounce from "lodash/debounce";
import classNames from "classnames";

import { loadStory } from "../../../actions/storiesStream";
import { BREAKPOINT_DOWN_SMALL, ENTITY_TYPE, BLOCK_TYPE, FACTS_MATTER_TEMPLATE } from "../../../config/constants";
import { styleToHTML, blockToHTML } from "./conversionHelpers";
import { entityToHTML } from "../../../utils/entityToHTML";
import { handleEmbed } from "../../../utils/embed";
import { isInternalURL } from "../../../utils/utils";
import { GET_STORY_ERROR } from "../../../constants/actionTypes";
import Log from "../../../utils/log";

import styles from "./StoryBody.css";
import keepReadingStyles from "../../../components/Library/KeepReading/KeepReading.css";

class StoryBody extends Component {

  constructor(props) {
    super(props);

    this.state = {
      isOpen: !!props.permalinkId && props.permalinkId === props.story.id,
      isFixed: false,
      isClient: false,
      shouldProcess: true,
    };

    this.onKeepReadingClick = this._onKeepReadingClick.bind(this);
  }

  componentDidMount() {
    this.mediaQuery = window.matchMedia(BREAKPOINT_DOWN_SMALL);
    this.scrollListener = debounce(this.handleScroll, 50);
    this.setState({ isClient: true });
  }

  componentDidUpdate() {
    if (!this.state.isClient || !this.processedBlocks || !this.state.shouldProcess) return;

    if (this.processedBlocks.blocks) {
      this.processedBlocks.blocks.filter((block) => block.type === BLOCK_TYPE.EMBED).map(handleEmbed);

      (selectn("story.blocks.entityMap", this.props) || [])
        .filter((entity) => entity.type.toLowerCase() === ENTITY_TYPE.LINK)
        .map((entity) => {
          const matches = isInternalURL(entity.data.url);
          if (matches && matches.length > 1) {
            this.addInternalClickEventHandler(matches[1]);
          }
        });
    }

    if (this.processedBlocks.hiddenBlocks) {
      this.processedBlocks.hiddenBlocks.filter((block) => block.type === BLOCK_TYPE.EMBED).map(handleEmbed);
    }

    this.setState({ shouldProcess: false });
  }

  componentWillUnmount() {
    this.removeListeners();
  }

  addInternalClickEventHandler = (identifier) => {
    let elements = document.getElementsByClassName(`${identifier}-${this.props.story.id}`);
    for (let i = 0; i < elements.length; i++) {
      elements[i].onclick = (e) => {
        e.preventDefault();
        this.loadInternalStory(identifier)
          .then((storyElement) => { this.scrollToStory(storyElement)})
          .catch(() => { window.location = elements[i].href });
      };
    }
  };

  loadInternalStory = (internalStoryId) => {
    return new Promise((resolve, reject) => {
      //if this is a newsletter, redirect to the permalink without bothering with api call
      if (this.props.storyType === "newsletter") {
        reject();
        return;
      }

      //otherwise, load the story into the stream
      this.props.loadStory({ story: internalStoryId }, this.props.story.id)
        .then((res) => {
          //this will usually happen if it's a RM link
          if (res.type === GET_STORY_ERROR) {
            reject();
          } else {
            setTimeout(() => {
              //scroll to the story if it's on the page. if not, permalink
              const storyElement = document.getElementById(internalStoryId);
              storyElement ? resolve(storyElement) : reject();
            }, 200);
          }
        });
    });
  }

  scrollToStory = (element) => {
    const bounds = element.getBoundingClientRect();
    window.scrollBy(0, bounds.top - 100);
  };

  processBlocks = (blocks) => {
    let newBlocks = [];
    let hiddenBlocks = [];
    let keepReading = null;
    let wordCount = 0;
    let styledQuoteAttributions = [];

    for (let i=0; i < blocks.length; i++) {
      if (!blocks[i]) continue;
      if (i !== 0 && blocks[i].type.toLowerCase() === BLOCK_TYPE.QUOTE_ATTRIBUTION &&
        blocks[i - 1].type.toLowerCase() === BLOCK_TYPE.STYLED_QUOTE) {
        styledQuoteAttributions.push(blocks[i].key);
      }
      if (blocks[i].type.toLowerCase() === BLOCK_TYPE.KEEP_READING) {
        keepReading = blocks[i];
      } else if (keepReading) {
        hiddenBlocks.push(blocks[i]);
        wordCount += blocks[i].text.split(" ").length;
      } else {
        newBlocks.push(blocks[i]);
      }
    }

    return {
      blocks: newBlocks,
      keepReadingBlock: keepReading,
      hiddenBlocks,
      wordCount,
      styledQuoteAttributions,
    };
  };

  addListeners = () => {
    this.mediaQuery.addListener(this.handleResize);

    if (this.mediaQuery.matches) {
      window.addEventListener("scroll", this.scrollListener);
    }
  };

  removeListeners = () => {
    this.mediaQuery.removeListener(this.handleResize);
    window.removeEventListener("scroll", this.scrollListener);
  };

  handleScroll = () => {
    if (this.processedBlocks.keepReadingBlock && this.state.isOpen) {
      const hidden = document.getElementById(`hidden-${this.props.story.id}`);
      if (hidden) {
        const isFixed = this.mediaQuery.matches && this.state.isOpen && !this.isKeepReadingVisible(hidden);
        if (this.state.isFixed !== isFixed) {
          this.setState({ isFixed });
        }
      }
    }
  };

  handleResize = (event) => {
    if (event.matches) {
      window.addEventListener("scroll", this.scrollListener);
    } else {
      if (this.state.isFixed) {
        this.setState({ isFixed: false });
      }
      window.removeEventListener("scroll", this.scrollListener);
    }
  };

  isKeepReadingVisible = (element) => {
    const bounds = element.getBoundingClientRect();
    return bounds.bottom < (window.innerHeight || document.documentElement.clientHeight);
  };

  _onKeepReadingClick = () => {
    const isOpen = !this.state.isOpen;
    this.setState({ isOpen });

    if (isOpen) {
      this.addListeners();
    }  else {
      this.removeListeners();

      // If next headline is not visible, scroll to just show it
      let bounds = this.keepReadingButton.getBoundingClientRect();
      let scrollValue = 275 + bounds.top - (window.innerHeight || document.documentElement.clientHeight);

      if (scrollValue > 0) {
        window.scrollBy(0, scrollValue);
      } else if (bounds.top < 60) {
        // If keep reading button is not visible, scroll so it just shows
        window.scrollBy(0, bounds.top - 60);
      }
    }
  }

  getHTML = (blocks, entityMap, extras) => {
    let html = false;
    try{
      html = convertToHTML({
        styleToHTML,
        blockToHTML: blockToHTML(extras, this.state.isClient),
        // This shouldn't normally work, but we've tricked draft-convert into requiring a version of draft-js where
        // convertToRaw is a noop - thus we can pass raw draft data right into draft-convert
        entityToHTML: entityToHTML(this.props.story, styles),
      })({ blocks, entityMap });
    } catch(e){
      Log.logger(`draft-convert failure. story id: ${this.props.story.id}`, "error", { id: this.props.story.id, error: e });
    }
    return html;
  };

  render() {
    const currentBlocks = selectn("story.blocks.blocks", this.props);
    if (!currentBlocks) return null;
    this.processedBlocks = this.processBlocks(currentBlocks);
    const { blocks, hiddenBlocks, keepReadingBlock, wordCount, styledQuoteAttributions } = this.processedBlocks;
    const { entityMap } = this.props.story.blocks;
    const extras = { styledQuoteAttributions };

    const html = this.getHTML(blocks, entityMap, extras);
    const keepReadingHTML = keepReadingBlock && this.getHTML([keepReadingBlock], entityMap, { wordCount });
    const hiddenHTML = hiddenBlocks && this.getHTML(hiddenBlocks, entityMap, extras);

    // don't render the story if draft-convert failed
    if (!html || (keepReadingHTML && !hiddenHTML)) return null;

    return (
      <div className={
        classNames(
          styles.root,
          { [styles.factsMatter]: this.props.story.template === FACTS_MATTER_TEMPLATE }
        )
      }>
        <div dangerouslySetInnerHTML={{ __html: html }} />
        {keepReadingHTML && <div
          dangerouslySetInnerHTML={{ __html: keepReadingHTML }}
          onClick={this.onKeepReadingClick}
          ref={(node) => {this.keepReadingButton = node}}
          className={
            classNames(
              "qa-keep-reading",
              {
                [keepReadingStyles.keepReadingOpen]: this.state.isOpen,
                [keepReadingStyles.keepReadingFixed]: this.state.isFixed,
              })
          }
        />}
        {
          hiddenHTML &&
          <div
            id={`hidden-${this.props.story.id}`}
            style={{ display: this.state.isOpen ? "block" : "none", paddingBottom: "65px" }}
            aria-hidden={!this.state.isOpen}
            aria-expanded={this.state.isOpen}
            dangerouslySetInnerHTML={{ __html: hiddenHTML }} />
        }
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    stream: selectn("navigation.data.section.slug", state),
  };
};

StoryBody.propTypes = {
  story: PropTypes.object.isRequired,
  storyType: PropTypes.oneOf(["newsletter"]),
  permalinkId: PropTypes.string,
};

export default connect(mapStateToProps, { loadStory })(StoryBody);
